import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ImageSourcePropType } from 'react-native';

import {
    AddZonePayload,
    ZoneModal,
    RetrieveZoneDetails,
    InitiateProvisioningModal,
    CompleteProvisioningModal,
    CustomControlProfile,
    UpdateControlProfile,
} from '../../types/ZoneManagement';
import { RootState } from '../../app/store';

export enum DeviceStatus {
    HEALTHY = 'Healthy',
    UNHEALTHY = 'Unhealthy',
    UNKNOWN = 'Unknown',
}

// Define types instead of interfaces
export type ZoneItem = {
    id: string;
    name: string;
    meshId?: string;
    controlProfileName: string;
    deviceCount: number;
    deviceList: DeviceListItem[];
};

export type DeviceListItem = {
    key: string;
    status?: DeviceStatus;
    imageUrl?: ImageSourcePropType;
    deviceId?: string;
    macAddress?: string;
    name?: string;
    deviceKey?: string;
    unicastAddress?: number;
    displayId?: string;
    secretKey?: string;
    deviceType?: number;
    model?: string;
    deviceModel?: string;
    deviceTypeName?: string;
    weeklyControlProfileVersion?: string;
    zoneId?: string;
    /** Indicates if the device firmware is compatible with OTA upgrade */
    isOtaCompatible?: boolean;
    /** Current firmware version of the device */
    firmwareVersion?: string;
};

export type SelectedSchedule = {
    id: number;
    name: string;
    zoneAddress?: string;
    zoneId?: string;
    uniqueKey: string; // Unique identifier combining zoneId and controlProfile id
};

export type DeviceListResponse = {
    message: string;
    data: {
        deviceIds: DeviceListItem[];
    };
};
// Simplified mesh devices payload stored in Redux
export type MeshDevicesUnderMesh = {
    meshDeviceId: string;
    devices: DeviceListItem[];
};

export type GetZoneList = {
    meshId: string;
    zoneList: ZoneItem[];
    applicationKey: string;
    networkKey: string;
};
type DeleteZoneModalState = {
    isDeleteModalVisible: boolean;
};
type DeleteFixtureModalState = {
    isDeleteModalForFixtureVisible?: boolean;
};
export type ZoneDimmingPayload = {
    id?: string;
    startDate?: string;
    dimmingPercentage?: number;
    endDate?: string;
    dimmingStartDate?: string;
    dimmingMode?: number;
    scheduleName?: string;
    shiftEndTime?: string;
    scheduleDuration?: string;
};
type ZoneState = {
    addZoneModal: AddZonePayload;
    zoneDetails: ZoneModal;
    retriveZoneDetails: RetrieveZoneDetails;
    setZoneList: GetZoneList;
    setDeleteZoneModal: DeleteZoneModalState;
    setDeleteFixtureModal: DeleteFixtureModalState;
    editDeviceNameModalVisible: boolean;
    manualDimmingDetails: ZoneDimmingPayload;
    deviceListUnderZone: DeviceListResponse;
    createControlProfile: CustomControlProfile;
    getControlProfileDetails: CustomControlProfile;
    deviceListItem: DeviceListItem;
    controlProfileGroupList: ControlProfileGetGroupList[]; // Changed to array
    updateControlProfile: UpdateControlProfile;
    deviceListUnderMesh: MeshDevicesUnderMesh;
    selectedSchedule: SelectedSchedule | null;
};

type ControlProfileGetGroupList = {
    id: number;
    name: string;
    profileType: number;
    isActive: boolean;
};

const initialManualDimmingState: ZoneDimmingPayload = {
    dimmingPercentage: 0,
    id: '',
    dimmingStartDate: '',
    startDate: '',
    endDate: '',
    dimmingMode: 0,
};
// Initial values
const initialAddZonePayload: AddZonePayload = {
    name: '',
    groupId: '',
    meshId: '',
};

const initialZoneDetails: ZoneModal = {
    id: '',
    zoneName: '',
    zoneAddress: '',
};

const initialDeviceListItem: DeviceListItem = {
    deviceId: '',
    macAddress: '',
    name: '',
    deviceKey: '',
    unicastAddress: 0,
    displayId: '',
    secretKey: '',
    key: '',
    status: DeviceStatus.HEALTHY,
};

const initialRetrivelZoneDetails: RetrieveZoneDetails = {
    id: '',
    name: '',
    meshId: '',
    controlProfileName: '',
    deviceCount: 0,
    firmwareVersion: '',
    controlProfileId: '',
    zoneAddress: '',
    sensorCount: 0,
    weeklyControlProfileVersion: 1,
    isWeeklyControlProfileSynced: true,
    initiateProvisioning: {
        provisionerUnicast: 0,
        nextUnicastAddress: 0,
        ivIndex: 0,
        nextSequenceNumber: 0,
        isMeshEnabled: false,
        provisionerMacAddress: '',
        processType: 0,
    },
    completeProvisioning: {
        nextUnicastAddress: 0,
        ivIndex: 0,
        nextSequenceNumber: 0,
    },
    dimmingDetails: {
        dimmingPercentage: 10,
        startDate: '2025-09-01T14:08:42.04Z',
        endDate: '2025-09-01T14:08:42.04Z',
        dimmingMode: 0,
    },
};

const initialGetZoneList: GetZoneList = {
    meshId: '',
    zoneList: [],
    applicationKey: '',
    networkKey: '',
};

const initialDeleteZoneModalState: DeleteZoneModalState = {
    isDeleteModalVisible: false,
};
const initialDeleteFixtureModalState: DeleteFixtureModalState = {
    isDeleteModalForFixtureVisible: false,
};
const initialDeviceListUnderZone: DeviceListResponse = {
    message: '',
    data: {
        deviceIds: [],
    },
};
const initialDeviceListUnderMesh: MeshDevicesUnderMesh = {
    meshDeviceId: '',
    devices: [],
};
const initialCustomControlProfile: CustomControlProfile = {
    zoneId: '',
    controlProfileId: 0,
    scheduleName: '',
    occupancyDimming: false,
    photoCellDimming: false,
    timeScheduleDimming: false,
    occupancyDetectionWaitTime: 0,
    dayLightHarvesting: false,
    dayLightHarvestingGainValue: 0,
    targetLight: 0,
    photoControlWaitTime: 0,
    scheduleOverheatProtection: false,
    scheduleVirtualSensing: false,
    scheduleHighTrim: 100,
    minBrightness: 0,
    note: '',
    version: '1',
    dawnToDuskNoOccupancy: 0,
    duskToDawnNoOccupancy: 0,
    dawnToDuskOccupancy: 0,
    duskToDawnOccupancy: 0,
    minBrightnessLevelValue: 0,
    lightModeType: 1,
    firmwareLightModeType: 3,
    timeSyncLost: false,
    zoneOccupiedBrightnessLevel: 0,
    zoneNotOccupiedBrightnessLevel: 0,
    daylightHarvestingMinSlot1: 0,
    daylightHarvestingMaxSlot1: 0,
    daylightHarvestingMinSlot2: 0,
    daylightHarvestingMaxSlot2: 0,
    daylightHarvestingMinSlot3: 0,
    daylightHarvestingMaxSlot3: 0,
    daylightHarvestingMinSlot4: 0,
    daylightHarvestingMaxSlot4: 0,
    scheduleDayDetails: [],
};
const initialUpdateControlProfile: UpdateControlProfile = {
    zoneId: '',
    scheduleId: 0,
    scheduleName: '',
    occupancyDimming: false,
    photoCellDimming: false,
    timeScheduleDimming: false,
    occupancyDetectionWaitTime: 0,
    dayLightHarvesting: false,
    dayLightHarvestingGainValue: 0,
    targetLight: 0,
    photoControlWaitTime: 0,
    scheduleOverheatProtection: false,
    scheduleVirtualSensing: false,
    scheduleHighTrim: 100,
    note: '',
    scheduleDayDetails: [],
};

const initialControlProfileGroupList: ControlProfileGetGroupList[] = []; // Changed to empty array

// Combine into initial state
const initialState: ZoneState = {
    addZoneModal: initialAddZonePayload,
    zoneDetails: initialZoneDetails,
    retriveZoneDetails: initialRetrivelZoneDetails,
    setZoneList: initialGetZoneList,
    setDeleteZoneModal: initialDeleteZoneModalState,
    setDeleteFixtureModal: initialDeleteFixtureModalState,
    editDeviceNameModalVisible: false,
    manualDimmingDetails: initialManualDimmingState,
    deviceListUnderZone: initialDeviceListUnderZone,
    createControlProfile: initialCustomControlProfile,
    getControlProfileDetails: initialCustomControlProfile,
    deviceListItem: initialDeviceListItem,
    controlProfileGroupList: initialControlProfileGroupList,
    updateControlProfile: initialUpdateControlProfile,
    deviceListUnderMesh: initialDeviceListUnderMesh,
    selectedSchedule: null,
};

const zoneSlice = createSlice({
    name: 'zone',
    initialState,
    reducers: {
        setAddZonePayload: (state, action: PayloadAction<AddZonePayload>) => {
            state.addZoneModal = action.payload;
        },
        setZoneDetails: (state, action: PayloadAction<RetrieveZoneDetails>) => {
            state.zoneDetails = action.payload;
        },
        setZoneList: (state, action: PayloadAction<GetZoneList>) => {
            state.setZoneList = action.payload;
        },
        setRetriveZoneDetails: (state, action: PayloadAction<RetrieveZoneDetails>) => {
            state.retriveZoneDetails = action.payload;
        },
        setManualDimming: (state, action: PayloadAction<ZoneDimmingPayload>) => {
            state.manualDimmingDetails = action.payload;
        },
        setInitiateProvisioning: (state, action: PayloadAction<InitiateProvisioningModal>) => {
            state.retriveZoneDetails.initiateProvisioning = action.payload;
        },
        setCompleteProvisioning: (state, action: PayloadAction<CompleteProvisioningModal>) => {
            state.retriveZoneDetails.completeProvisioning = action.payload;
        },
        setDeleteModalVisible: (state, action) => {
            state.setDeleteZoneModal.isDeleteModalVisible = action.payload;
        },
        setDeleteModalForFixtureVisible: (state, action) => {
            state.setDeleteFixtureModal.isDeleteModalForFixtureVisible = action.payload;
        },
        setEditDeviceNameModalVisible: (state, action) => {
            state.editDeviceNameModalVisible = action.payload;
        },
        setDeviceListUnderZone: (state, action: PayloadAction<DeviceListResponse>) => {
            state.deviceListUnderZone = action.payload;
        },
        setCreateControlProfile: (state, action: PayloadAction<CustomControlProfile>) => {
            state.createControlProfile = action.payload;
        },
        setRetriveControlProfileDetails: (state, action: PayloadAction<CustomControlProfile>) => {
            state.getControlProfileDetails = action.payload;
        },
        setDeviceListItem: (state, action: PayloadAction<DeviceListItem>) => {
            state.deviceListItem = action.payload;
        },
        setControlProfileGroupList: (state, action: PayloadAction<ControlProfileGetGroupList[]>) => {
            state.controlProfileGroupList = action.payload;
        },
        setUpdateControlProfile: (state, action: PayloadAction<UpdateControlProfile>) => {
            state.updateControlProfile = action.payload;
        },
        setDeviceListUnderMesh: (state, action: PayloadAction<MeshDevicesUnderMesh>) => {
            state.deviceListUnderMesh = action.payload;
        },
        setSelectedSchedule: (state, action: PayloadAction<SelectedSchedule | null>) => {
            state.selectedSchedule = action.payload;
        },
    },
});

// Export actions
export const {
    setAddZonePayload,
    setZoneDetails,
    setZoneList,
    setRetriveZoneDetails,
    setDeleteModalVisible,
    setDeleteModalForFixtureVisible,
    setEditDeviceNameModalVisible,
    setManualDimming,
    setInitiateProvisioning,
    setCompleteProvisioning,
    setDeviceListUnderZone,
    setCreateControlProfile,
    setRetriveControlProfileDetails,
    setDeviceListItem,
    setControlProfileGroupList,
    setUpdateControlProfile,
    setDeviceListUnderMesh,
    setSelectedSchedule,
} = zoneSlice.actions;
// Export selectors
export const selectAddZoneModal = (state: RootState): AddZonePayload => state.zone.addZoneModal;
export const selectZoneDetails = (state: RootState): RetrieveZoneDetails => state.zone.zoneDetails;
export const selectRetriveZoneDetails = (state: RootState): RetrieveZoneDetails => state.zone.retriveZoneDetails;
export const selectSetZoneList = (state: RootState): GetZoneList => state.zone.setZoneList;
export const selectManualDimmingDetails = (state: RootState): ZoneDimmingPayload => state.zone.manualDimmingDetails;
export const selectSetIntiateProvisioning = (state: RootState): InitiateProvisioningModal =>
    state.zone.retriveZoneDetails.initiateProvisioning;
export const selectSetCompleteProvisioning = (state: RootState): CompleteProvisioningModal =>
    state.zone.retriveZoneDetails.completeProvisioning;
export const selectDeviceListUnderZone = (state: RootState): DeviceListItem[] =>
    state.zone.deviceListUnderZone?.data?.deviceIds;
export const selectCreateControlProfile = (state: RootState): CustomControlProfile => state.zone.createControlProfile;
export const selectGetControlProfileDetails = (state: RootState): CustomControlProfile =>
    state.zone.getControlProfileDetails;
export const selectedDeviceListItem = (state: RootState): DeviceListItem => state.zone.deviceListItem;
export const selectControlProfileGroupList = (state: RootState): ControlProfileGetGroupList[] =>
    state.zone.controlProfileGroupList;
export const selectUpdateControlProfile = (state: RootState): UpdateControlProfile => state.zone.updateControlProfile;
export const selectDeviceListUnderMesh = (state: RootState): MeshDevicesUnderMesh => state.zone.deviceListUnderMesh;
export const selectSelectedSchedule = (state: RootState): SelectedSchedule | null => state.zone.selectedSchedule;
// Export reducer
export default zoneSlice;
